import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { Button } from "@fp/ui/components/base/buttons/button";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UserList } from "@fp/ui/components/users/user-list";
import {
  getGetRoleByIdQueryKey,
  getGetRoleByIdQueryOptions,
  getGetRolesQueryKey,
  getGetUsersQueryOptions,
  useAddUsersToRole,
} from "@fp/admin-api-client/client";

export const Route = createFileRoute("/settings/roles/$id/edit")({
  loader: async ({ context: { queryClient }, params }) => {
    const role = await queryClient.fetchQuery(
      getGetRoleByIdQueryOptions(params.id),
    );
    const users = await queryClient.fetchQuery(
      getGetUsersQueryOptions({ page: 1, pageSize: 50 }),
    );
    return {
      role: role.data,
      users: users.data.items ?? [],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const router = useRouter();
  const { id } = Route.useParams();
  const { role, users } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );

  const addUsersToRoleMutation = useAddUsersToRole({
    mutation: {
      onSuccess: () => {
        // Invalidate and refetch the role data
        queryClient.invalidateQueries({
          queryKey: getGetRoleByIdQueryKey(id),
        });
        queryClient.invalidateQueries({
          queryKey: getGetRolesQueryKey(),
        });
        // Navigate back to the role detail page
        router.navigate({
          to: `/settings/roles/${id}`,
        });
      },
      onError: (error) => {
        console.error("Error adding users to role:", error);
        // TODO: Show error toast/notification
      },
    },
  });

  const handleContinue = useCallback(() => {
    const userIdsArray = Array.from(selectedUserIds);
    if (userIdsArray.length === 0) {
      return; // No users selected
    }

    addUsersToRoleMutation.mutate({
      id,
      data: {
        userIds: userIdsArray,
      },
    });
  }, [id, selectedUserIds, addUsersToRoleMutation]);

  // Get role title for display
  const roleTitle = role.name;

  return (
    <AdminLayout>
      <AdminPageHeader
        title={`Add ${roleTitle}`}
        parent={{
          url: router.buildLocation({
            to: "/settings/roles/$id",
            params: { id },
          }).href,
          label: roleTitle,
        }}
      />
      <PageContentContainer className="space-y-4 p-0">
        {/* Contact List */}
        <UserList
          users={users.filter(
            (user) => !role.users.some((rUser) => rUser.id === user.id),
          )}
          selectedUserIds={selectedUserIds}
          onSelect={(userIds: string[]) => setSelectedUserIds(new Set(userIds))}
          className="h-[400px] border-t border-b"
        />

        {/* Continue Button */}
        <div className="p-4 text-center bg-white">
          <Button
            color="primary"
            size="lg"
            className="w-full max-w-md"
            onClick={handleContinue}
            disabled={
              selectedUserIds.size === 0 || addUsersToRoleMutation.isPending
            }
          >
            {addUsersToRoleMutation.isPending ? "Adding..." : "Continue"}
          </Button>
        </div>
      </PageContentContainer>
    </AdminLayout>
  );
}
